import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useLoaderStore } from '@/stores/loader'
import { useUserStore } from '@/stores/user'
import { useSnackbarStore } from '@/stores/snackbar'
import { useMediaQuery, useStorage } from '@vueuse/core'
import { track } from '@/helpers/mixpanelDes'
import { coverDimensions } from '@/constants/coverDimensions'
import { http, HTTPError, isAbortError } from '@/helpers/http'
import type {
  TProject,
  TProjectFontsResponse,
  TProjectsPagination,
  TProjectsResponse,
  TProjectsSection,
  TRecentItem,
  TSortOptions,
  TUserSortOptions,
} from '@/types/projects'
import type { TClient } from '@/types/agency'
import type { TCoverSize } from '@/types/cover'

const ROOT_SECTION_ID = 0

export const useProjectsStore = defineStore('projects', () => {
  const sections = ref<TProjectsSection[]>([])
  const sectionsSortBy = ref<string>('newest')
  const projects = ref<TProject[]>([])
  const recentItems = ref<TRecentItem[]>([])
  const myTemplates = ref<TProject[]>([])
  const projectsPage = ref<number>(1)
  const projectsPagination = ref<TProjectsPagination | null>(null)
  const projectsSortBy = ref<string>('newest')
  const projectToPublish = ref<TProject | null>(null)
  const section = ref<TProjectsSection | null>(null)
  const sortOptions = ref<TUserSortOptions>({})
  const rootProjectsCount = ref<number>(0)
  const user = useUserStore()
  const loader = useLoaderStore()
  const snackbar = useSnackbarStore()
  const myTemplatesSearch = ref<string>('')
  const myTemplatesSortOptions = [
    { label: 'Newest', name: 'newest', icon: 'fa-clock' },
    { label: 'Oldest', name: 'oldest', icon: 'fa-clock' },
    { label: 'A-Z', name: 'a-z', icon: 'fa-clock' },
    { label: 'Z-A', name: 'z-a', icon: 'fa-clock' },
  ]
  const myTemplatesSort = ref(myTemplatesSortOptions[0])
  const pageDimensions = ref<TCoverSize[]>(coverDimensions)
  const loadedSectionId = ref<number | null>(null)
  let pendingLoad: Promise<void> | null = null

  function invalidate(): void {
    loadedSectionId.value = null
  }

  async function fetchSections(sortBy: string = '') {
    sortOptions.value = useStorage('sortOptions', {} as TUserSortOptions).value

    if (sortBy !== '') {
      sectionsSortBy.value = sortBy
      if (!sortOptions.value[user.user.id]) {
        sortOptions.value[user.user.id] = <TSortOptions>{}
      }
      sortOptions.value[user.user.id]!.folders = sortBy
    } else {
      sectionsSortBy.value = sortOptions.value[user.user.id]?.folders || 'newest'
    }

    const res = await fetch(`/projects_sections/list/${sectionsSortBy.value}`)
    if (!res.ok) {
      throw new Error(`Failed to fetch sections`)
    }
    sections.value = await res.json()
  }

  async function deleteSection(sectionId: number) {
    const res = await fetch('/projects_sections/remove', {
      method: 'POST',
      body: JSON.stringify({
        sectionId: sectionId,
      }),
    })
    if (!res.ok) {
      throw new Error(`Failed to delete section`)
    }
    const data = await res.json()
    if (data.result === true) {
      const index = sections.value.findIndex((item) => item.id === sectionId)
      if (index >= 0) {
        sections.value.splice(index, 1)
      }
    }
  }

  async function deleteSections(sectionsToDelete: TProjectsSection[]) {
    const res = await fetch('/projects_sections/remove', {
      method: 'POST',
      body: JSON.stringify({
        sectionId: sectionsToDelete.map((item: TProjectsSection) => {
          return item.id
        }),
      }),
    })
    if (!res.ok) {
      throw new Error(`Failed to delete section`)
    }
    const data = await res.json()
    if (data.result === true) {
      sectionsToDelete.forEach((section) => {
        const index = sections.value.findIndex((item) => item.id === section.id)
        if (index >= 0) {
          sections.value.splice(index, 1)
        }
      })
    }
  }

  async function renameSection(sectionId: number, name: string) {
    const res = await fetch('/projects_sections/rename', {
      method: 'POST',
      body: JSON.stringify({
        sectionId: sectionId,
        name,
      }),
    })
    if (!res.ok) {
      throw new Error(`Failed to rename section`)
    }
  }

  async function fetchProjects(
    sectionId: number = ROOT_SECTION_ID,
    nextPage: boolean = false,
    sortBy: string = '',
    query: string = '',
  ) {
    sortOptions.value = useStorage('sortOptions', {} as TUserSortOptions).value

    if (sortBy !== '') {
      projectsSortBy.value = sortBy
      if (!sortOptions.value[user.user.id]) {
        sortOptions.value[user.user.id] = <TSortOptions>{}
      }
      sortOptions.value[user.user.id]!.projects = sortBy
    } else {
      projectsSortBy.value =
        sortOptions.value[user.user.id]?.projects || (sectionId > ROOT_SECTION_ID ? 'custom' : 'newest')
    }

    if (nextPage === false) {
      projectsPage.value = 1
      projects.value = []
      section.value = null
      projectsPagination.value = null
    }

    if (projectsSortBy.value === 'custom' && sectionId === ROOT_SECTION_ID) {
      projectsSortBy.value = 'newest'
    }

    const endpoint = `/projects_sections/${sectionId}`
    const params = {
      sortBy: projectsSortBy.value,
      page: projectsPage.value,
      query: query,
    }

    let data: TProjectsResponse
    try {
      data = await http.post<TProjectsResponse>(endpoint, params)
    } catch (error) {
      if (error instanceof HTTPError) {
        throw new Error(
          `Failed to fetch projects from section ${sectionId} - Status: ${error.status} - ${error.response}`,
          { cause: error },
        )
      }
      throw error
    }

    if (projectsPage.value > 1) {
      projects.value = projects.value.concat(data.results)
    } else {
      projects.value = data.results
    }
    if (data.section) {
      section.value = data.section
    }
    rootProjectsCount.value = data.root_count || 0

    projectsPagination.value = data.pagination || null
    // a filtered result is not the full section listing, so it must not satisfy ensureProjects()
    loadedSectionId.value = query ? null : sectionId
  }

  async function ensureProjects(): Promise<void> {
    if (loadedSectionId.value === ROOT_SECTION_ID) {
      return
    }
    if (pendingLoad) {
      return pendingLoad
    }

    pendingLoad = fetchProjects()
    try {
      await pendingLoad
    } finally {
      pendingLoad = null
    }
  }

  async function generatePdfThumbnailForProject(project: TProject): Promise<string> {
    try {
      const res = await http.post<{ medium_thumbnail?: string }>('/generate_pdf_thumbnail_for_project', { project })
      if (!res?.medium_thumbnail) throw new Error(`Failed to generate pdf thumbnail for project`)
      return res.medium_thumbnail
    } catch {
      return ''
    }
  }

  async function fetchProject(slugId: string): Promise<TProject | null> {
    try {
      const res = await http.get<TProject | null>(`/projects/${slugId}?id=true`)
      if (!res) throw new Error(`Failed to fetch project`)
      return res
    } catch {
      return null
    }
  }

  async function fetchRecentItems() {
    try {
      recentItems.value = await http.get<TRecentItem[]>(`/projects/recent-items/14`)
    } catch (error) {
      if (isAbortError(error)) {
        return
      } else if (error instanceof HTTPError) {
        throw new Error(`Failed to fetch recent items - Status: ${error.status} - ${error.response}`, {
          cause: error,
        })
      }
      throw error
    }
  }

  function removeRecentItem(source: TRecentItem['source'], id: number) {
    const index = recentItems.value.findIndex((item) => item.source === source && item.id === id)
    if (index >= 0) {
      recentItems.value.splice(index, 1)
    }
  }

  async function fetchMyTemplates() {
    try {
      myTemplates.value = await http.get<TProject[]>(`/pr-templates-new/`)
    } catch (error) {
      if (error instanceof HTTPError) {
        throw new Error(`Failed to fetch recent projects - Status: ${error.status} - ${error.response}`, {
          cause: error,
        })
      }
      throw error
    }
  }

  async function fetchNextProjects(sectionId: number) {
    projectsPage.value++
    return await fetchProjects(sectionId, true)
  }

  async function duplicateProject(project: TProject, copyName: string) {
    const res = await fetch('/duplicate_project/', {
      method: 'POST',

      body: JSON.stringify({
        newProjectName: copyName,
        oldProjectName: project.name,
        projectId: project.id,
      }),
    })
    if (!res.ok) {
      throw new Error(`Failed to duplicate project`)
    }
    const data = await res.json()
    if (data.status === 'success') {
      fetchProjects(section?.value?.id ? section.value.id : 0, false)
    }
    return data
  }

  function findIndex(project: TProject, proTemplateType?: boolean): number {
    const toSearch = proTemplateType ? myTemplates : projects
    return toSearch.value.findIndex((item) => {
      return project.id === item.id
    })
  }

  async function deleteProject(
    project: TProject,
    proTemplateType?: boolean,
  ): Promise<{ success: boolean; error?: string }> {
    const index = findIndex(project, proTemplateType)

    const res = await fetch(`/projects/${project.id}`, {
      method: 'DELETE',
    })

    if (!res.ok) {
      const errorMessage = await res.text()
      snackbar.add(errorMessage || 'Failed to delete project', { type: 'danger', permanent: true })
      return { success: false, error: errorMessage }
    }

    if (proTemplateType) {
      if (index >= 0) {
        myTemplates.value.splice(index, 1)
      }
    } else {
      if (index >= 0) {
        projects.value.splice(index, 1)
        if (projectsPagination.value) {
          projectsPagination.value.total--
        }
        fetchSections()
      }
      removeRecentItem('project', project.id)
    }

    track('deleted-project', {
      name: project.name,
      id: project.id,
    })

    return { success: true }
  }

  async function deleteProjects(projectsToDelete: TProject[]) {
    loader.show('Deleting projects...')
    const ids = projectsToDelete.map((item) => {
      return item.id
    })
    if (ids.length > 0) {
      const res = await fetch('/projects/delete', {
        method: 'POST',
        body: JSON.stringify({
          ids,
        }),
      })
      const data = await res.json()
      const deletedIds: number[] = data.deleted ?? (data.result === true ? ids : [])

      for (let i = 0; i < deletedIds.length; i++) {
        const index = projects.value.findIndex((item) => {
          return deletedIds[i] === item.id
        })
        if (index >= 0) {
          projects.value.splice(index, 1)
          if (projectsPagination.value) {
            projectsPagination.value.total--
          }
        }
      }

      if (!res.ok) {
        snackbar.add(data.msg || 'Failed to delete projects', { type: 'danger', permanent: true })
      }
    }
    loader.hide()
  }

  async function renameProject(id: number, name: string) {
    const res = await fetch('/projects/rename_project', {
      method: 'POST',
      body: JSON.stringify({
        id,
        name,
      }),
    })
    if (!res.ok) {
      throw new Error(`Failed to rename project`)
    }
    return await res.json()
  }

  async function createFolder(name: string) {
    const res = await fetch('/projects_sections/add', {
      method: 'POST',
      body: JSON.stringify({
        id: 0,
        name,
      }),
    })
    if (!res.ok) {
      throw new Error(`Failed to create folder`)
    }
    fetchSections()
    return await res.json()
  }

  async function moveToFolder(projectId: number, sectionId: number) {
    const res = await fetch('/projects_sections/move', {
      method: 'POST',
      body: JSON.stringify({
        projectId,
        sectionId,
      }),
    })
    if (!res.ok) {
      const errorData = await res.json().catch(() => null)
      throw new Error(errorData?.error || 'Failed to move project to folder')
    }
    const data = await res.json()
    if (data.result === true && sectionId != section.value?.id) {
      const index = projects.value.findIndex((item) => {
        return item.id === projectId
      })
      if (index >= 0) {
        projects.value.splice(index, 1)
        if (projectsPagination.value) {
          projectsPagination.value.total--
        }
      }
    }
  }

  async function moveManyToFolder(projectsToMove: TProject[], sectionId: number) {
    const ids = projectsToMove.map((item) => {
      return item.id
    })

    const res = await fetch('/projects_sections/move_many', {
      method: 'POST',
      body: JSON.stringify({
        projectIds: ids,
        sectionId,
      }),
    })
    if (!res.ok) {
      const errorData = await res.json().catch(() => null)
      throw new Error(errorData?.error || 'Failed to move projects to folder')
    }
    const data = await res.json()
    if (data.result === true && sectionId != section.value?.id) {
      for (let i = 0; i < ids.length; i++) {
        const index = projects.value.findIndex((item) => {
          return ids[i] === item.id
        })
        if (index >= 0) {
          projects.value.splice(index, 1)
          if (projectsPagination.value) {
            projectsPagination.value.total--
          }
        }
      }
    }
  }

  async function regeneratePDF(project: TProject) {
    loader.show('Please wait...')
    const res = await fetch('/html2pdf/regen_pdf', {
      method: 'POST',
      body: JSON.stringify({
        project_id: project.id,
      }),
    })
    loader.hide()
    const data = await res.json()
    if (!res.ok) {
      if (data.message) {
        throw new Error(data.message)
      }
      throw new Error(`Failed to regenerate PDF`)
    }
    return data
  }

  async function getPDFs(project: TProject) {
    const res = await fetch('/html2pdf/getpdfs', {
      method: 'POST',
      body: JSON.stringify({
        project_id: project.id,
      }),
    })
    if (!res.ok) {
      throw new Error(`Failed to get PDFs`)
    }
    const data = await res.json()
    return data.data
  }

  async function setOrder(project: TProject) {
    const order: { [x: number]: number } = {}
    projects.value.forEach((project, index) => {
      order[project.id] = index + 1
    })
    const res = await fetch('/projects_sections/move', {
      method: 'POST',
      body: JSON.stringify({
        order,
        projectId: project.id,
        sectionId: section.value?.id,
      }),
    })
    if (!res.ok) {
      throw new Error(`Failed to set order`)
    }
  }

  async function setFoldersOrder() {
    const order: number[] = sections.value.map((item) => {
      return item.id
    })

    const res = await fetch('/projects_sections/reorder', {
      method: 'POST',
      body: JSON.stringify({
        order,
      }),
    })
    if (!res.ok) {
      throw new Error(`Failed to reorder sections`)
    }
  }

  async function saveAsTemplate(projectId: number, name: string) {
    const res = await fetch('/pr-templates/convertProjectIntoTemplate', {
      method: 'POST',
      body: JSON.stringify({
        name,
        projectId,
      }),
    })
    if (!res.ok && res.status !== 400) {
      throw new Error(`Failed to save project as template`)
    }
    return await res.json()
  }

  async function restoreLocalBackup(projectId: number, html: string, css: string) {
    const res = await fetch('/projects/revert-project-local-backup', {
      method: 'POST',
      body: JSON.stringify({
        projectId,
        html,
        css,
      }),
    })
    if (!res.ok) {
      throw new Error(`Failed to restore a backup`)
    }
    return await res.text()
  }

  async function restoreRemoteBackup(entryId: number) {
    const res = await fetch(`/projects/${entryId}/revert-project`, {
      method: 'POST',
    })
    if (!res.ok) {
      throw new Error(`Failed to restore a backup`)
    }
    return await res.text()
  }

  function goToBuilderAndEdit(slugId: string, slug: string) {
    const isMobile = useMediaQuery('(max-width: 1024px)')

    if (isMobile.value) {
      snackbar.add(
        'Project editing is not available on mobile devices. Please switch to desktop to access editing features',
      )
      return
    }

    location.href = `/app/#/builder/${slugId}/${slug}`
  }

  async function getClientProjects(projectId: number): Promise<TClient[]> {
    return await http.get<TClient[]>('/clients/project/' + projectId)
  }

  async function getPreview(): Promise<TProject | null> {
    try {
      const res = await http.get<{ success: boolean; project: TProject }>('/projects/get-preview')
      if (!res) throw new Error(`Failed to fetch project`)
      return res.project
    } catch {
      return null
    }
  }

  async function usePreview(): Promise<boolean> {
    try {
      await http.get('/projects/use-preview')
      invalidate()
      return true
    } catch {
      return false
    }
  }

  async function closePreview(): Promise<boolean> {
    try {
      await http.get('/projects/close-preview')
      invalidate()
      return true
    } catch {
      return false
    }
  }

  async function getUsedFonts(
    projectId: number,
    hash: string | null = null,
    coverId: number | null = null,
  ): Promise<{ fonts: string[]; customFontLinks: string[] }> {
    try {
      const query = hash ? `?hash=${hash}&coverId=${coverId ?? 0}` : ''
      const res = await http.get<TProjectFontsResponse>(`/projects/${projectId}/fonts${query}`)
      if (res.status !== 'success') throw new Error(res.message || `Failed to fetch used fonts`)
      return { fonts: res.fonts, customFontLinks: res.custom_font_links }
    } catch {
      return { fonts: [], customFontLinks: [] }
    }
  }

  const myTemplatesFilteredAndSorted = computed(() => {
    const filtered = !myTemplatesSearch.value.length
      ? myTemplates.value
      : myTemplates.value.filter((myTemplate) => {
          return myTemplate.name.toLowerCase().includes(myTemplatesSearch.value.toLowerCase())
        })

    return filtered.sort((a, b) => {
      if (myTemplatesSort.value?.name === 'newest') {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      } else if (myTemplatesSort.value?.name === 'oldest') {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      } else if (myTemplatesSort.value?.name === 'a-z') {
        return a.name.localeCompare(b.name)
      } else if (myTemplatesSort.value?.name === 'z-a') {
        return b.name.localeCompare(a.name)
      } else {
        return 0
      }
    })
  })

  function getStats(html?: string): { pages: number; chapters: number; words: number; readTime: number } {
    if (!html) {
      return { pages: 0, chapters: 0, words: 0, readTime: 0 }
    }

    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const wordsPerMinute = 200

    const pages = doc.querySelectorAll('.cover-page-container, .page-container, .back-page-container').length
    const chapterLabels = doc.querySelectorAll('.page-container:not(.toc-container) .chapter-heading-label').length
    const contentHeadings = [
      ...doc.querySelectorAll<HTMLElement>(
        '.page-container:not(.toc-container) h1, .page-container:not(.toc-container) h2, .page-container:not(.toc-container) h3, .page-container:not(.toc-container) h4, .page-container:not(.toc-container) h5, .page-container:not(.toc-container) h6',
      ),
    ].filter((heading) => {
      return heading.textContent?.trim() && !heading.closest('#footerwrap, #headerwrap')
    })
    const h2Chapters = contentHeadings.filter((heading) => heading.tagName === 'H2').length
    const chapters = chapterLabels || h2Chapters || contentHeadings.length
    const words = doc.body.textContent?.split(/\s+/).filter((word: string) => word.length > 0).length || 0
    const readTime = Math.ceil(words / wordsPerMinute)

    return { pages, chapters, words, readTime }
  }

  async function changePageSize(projectId: number, width: number, height: number): Promise<boolean> {
    try {
      const res = await http.post('/projects/changePageSize', { project_id: projectId, width, height }, true)
      if (res) {
        return true
      }
      return false
    } catch {
      return false
    }
  }

  return {
    sections,
    sectionsSortBy,
    projects,
    myTemplates,
    myTemplatesFilteredAndSorted,
    myTemplatesSearch,
    myTemplatesSort,
    myTemplatesSortOptions,
    projectsSortBy,
    pageDimensions,
    projectsPagination,
    section,
    generatePdfThumbnailForProject,
    rootProjectsCount,
    projectToPublish,
    getClientProjects,
    fetchSections,
    deleteSection,
    deleteSections,
    renameSection,
    fetchProjects,
    ensureProjects,
    invalidate,
    fetchProject,
    recentItems,
    fetchRecentItems,
    removeRecentItem,
    fetchMyTemplates,
    fetchNextProjects,
    duplicateProject,
    deleteProject,
    deleteProjects,
    renameProject,
    createFolder,
    moveToFolder,
    moveManyToFolder,
    regeneratePDF,
    getPDFs,
    setOrder,
    setFoldersOrder,
    saveAsTemplate,
    restoreLocalBackup,
    restoreRemoteBackup,
    goToBuilderAndEdit,
    getPreview,
    usePreview,
    closePreview,
    getStats,
    getUsedFonts,
    changePageSize,
  }
})
